import type {
  SemanticFormatSpec,
  SemanticMaterialitySpec,
} from "@/features/data-hub/model/semanticFormat";
import {
  buildFormatFromMeasurementValues,
  buildMaterialityFromMeasurementValues,
  createEmptyMeasurementFormValues,
  measurementToFormValues,
  type SemanticMeasurementFormValues,
} from "@/features/data-hub/model/semanticMeasurement";

export type SemanticMeasure = {
  key?: string;
  name?: string;
  description?: string;
  /** The complete value expression, aggregate included: `SUM(amount)`, `COUNT(*)`. */
  expression?: string;
  source_table?: string;
  source_ref?: Record<string, unknown>;
  entity?: string;
  /** SQL predicates ANDed into this measure, e.g. `status = 'completed'`. */
  filters?: string[];
  confidence?: number;
  provenance?: string;
  needs_clarification?: boolean;
  format?: SemanticFormatSpec;
  materiality?: SemanticMaterialitySpec;
};

export type SemanticMeasureFormValues = {
  name: string;
  description: string;
  expression: string;
  sourceTable: string;
  entity: string;
  /** One SQL predicate per line; blank lines are ignored. */
  filters: string;
  measurement: SemanticMeasurementFormValues;
};

export function slugifyMeasureKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_|_$/g, "");
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function resolveSourceTable(measure: SemanticMeasure): string {
  const fromSourceRef = asString(measure.source_ref?.table).trim();
  if (fromSourceRef) {
    return fromSourceRef;
  }
  return (measure.source_table ?? "").trim();
}

/** Matches a whole-expression aggregate so its inner operand can be read back out. */
const WHOLE_AGGREGATE = /^\s*(?:COUNT|SUM|AVG|MIN|MAX)\s*\(\s*(?:DISTINCT\s+)?([^()]*?)\s*\)\s*$/i;

/**
 * The bare column a simple aggregate expression measures, or "" when there is none.
 *
 * Mirrors the backend's `column_from_expression`. A conditional or derived expression
 * deliberately yields nothing: no single column backs it, and guessing one would write a
 * `source_ref.column` the SQL layer cannot honour.
 */
export function columnFromExpression(expression: string): string {
  const match = WHOLE_AGGREGATE.exec(expression.trim());
  const inner = match?.[1]?.trim() ?? "";
  if (!inner || inner === "*") {
    return "";
  }
  return inner.includes(".") ? (inner.split(".").pop() ?? "").trim() : inner;
}

/** One predicate per line, trimmed, blanks dropped. */
function parseFilterLines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export function measureToFormValues(
  measure: SemanticMeasure,
): SemanticMeasureFormValues {
  return {
    name: (measure.name ?? "").trim(),
    description: (measure.description ?? "").trim(),
    expression: (measure.expression ?? "").trim(),
    sourceTable: resolveSourceTable(measure),
    entity: (measure.entity ?? "").trim(),
    filters: (measure.filters ?? []).join("\n"),
    measurement: measurementToFormValues(measure.format, measure.materiality),
  };
}

export function createEmptyMeasureFormValues(): SemanticMeasureFormValues {
  return {
    name: "",
    description: "",
    expression: "",
    sourceTable: "",
    entity: "",
    filters: "",
    measurement: createEmptyMeasurementFormValues(),
  };
}

export function buildMeasureFromFormValues(
  values: SemanticMeasureFormValues,
  existing?: SemanticMeasure,
): SemanticMeasure {
  const trimmedName = values.name.trim();
  const key = (existing?.key ?? "").trim() || slugifyMeasureKey(trimmedName);
  const sourceTable = values.sourceTable.trim();
  const expression = values.expression.trim();
  // The bound column comes from inside the expression, not from the whole of it:
  // `SUM(AMOUNT)` binds AMOUNT, and `COUNT(*)` or a CASE expression binds nothing.
  const column = columnFromExpression(expression);
  const sourceRef: Record<string, unknown> = sourceTable
    ? { table: sourceTable, ...(column ? { column } : {}) }
    : column
      ? { column }
      : {};

  return {
    ...existing,
    key,
    name: trimmedName,
    description: values.description.trim() || undefined,
    expression: expression || undefined,
    source_table: sourceTable || undefined,
    source_ref: sourceRef,
    entity: values.entity.trim() || undefined,
    filters: parseFilterLines(values.filters),
    // Explicit rather than relying on the `...existing` spread: the form owns both
    // specs now, so an edit that clears one must actually clear it.
    format: buildFormatFromMeasurementValues(values.measurement),
    materiality: buildMaterialityFromMeasurementValues(values.measurement),
    confidence: 1,
    provenance: "interview",
  };
}

export function selectSemanticMeasuresFromGeneratedModel(
  generatedModel: Record<string, unknown>,
): SemanticMeasure[] {
  const document = asRecord(generatedModel.document);
  const legacyDraft = asRecord(generatedModel.draft);
  const fromDocument = document.measures;
  if (Array.isArray(fromDocument)) {
    return fromDocument as SemanticMeasure[];
  }
  const fromDraft = legacyDraft.measures;
  if (Array.isArray(fromDraft)) {
    return fromDraft as SemanticMeasure[];
  }
  return [];
}

export function isSemanticMeasureKeyTaken(
  measures: SemanticMeasure[],
  key: string,
  excludeIndex: number | null = null,
): boolean {
  const normalized = key.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return measures.some((measure, index) => {
    if (excludeIndex !== null && index === excludeIndex) {
      return false;
    }
    return (measure.key ?? "").trim().toLowerCase() === normalized;
  });
}
